import { CheckCircleIcon } from 'lucide-react';
import React, { useMemo, useEffect } from 'react';

type Props = {
  password?: string;
  onChangeStrength?: (strength: string) => void;
  onChangeProgress?: (progress: number) => void;
  onChangeRules?: (rules: Record<string, boolean>) => void;
};

export const PasswordValidator: React.FC<Props> = ({
  password = '',
  onChangeStrength,
  onChangeProgress,
  onChangeRules,
}) => {
  const rules = useMemo(() => {
    return {
      hasNumber: /\d/.test(password),
      minLength: password.length >= 12,
      hasUppercase: /[A-Z]/.test(password),
      hasSpecial: /[^A-Za-z0-9]/.test(password),
    };
  }, [password]);

  const totalRules = Object.keys(rules).length;
  const passedCount = Object.values(rules).filter(Boolean).length;

  const strength = !rules.minLength
    ? 'Сул'
    : passedCount <= 1
    ? 'Сул'
    : passedCount === 2
    ? 'Дунд'
    : 'Хүчтэй';

  const progress = !rules.minLength ? 0 : (passedCount / totalRules) * 100;

  const barColor = !rules.minLength
    ? 'bg-red-500'
    : strength === 'Сул'
    ? 'bg-red-500'
    : strength === 'Дунд'
    ? 'bg-yellow-500'
    : 'bg-green-500';

  // ------------------------
  // 🔥 CALLBACKS HERE
  // ------------------------
  useEffect(() => {
    onChangeStrength?.(strength);
    onChangeProgress?.(progress);
    onChangeRules?.(rules);
  }, [strength, progress, rules, onChangeStrength, onChangeProgress, onChangeRules]);

  const Item = ({ ok, label }: { ok: boolean; label: string }) => (
    <div className="flex items-center gap-2">
      {ok ? (
        <CheckCircleIcon className="w-5 h-5 text-green-500" />
      ) : (
        <span className="w-5 h-5 rounded-full border border-gray-300" />
      )}
      <span className={`text-sm ${ok ? 'text-green-600' : 'text-gray-500'}`}>{label}</span>
    </div>
  );

  return (
    <div className="bg-[#F5F6FA] rounded-lg p-4 space-y-3 w-[356px]">
      <div className="flex justify-between text-sm font-manrope">
        <span className="text-gray-600">Нууц үгийн хүч:</span>
        <span
          className={
            strength === 'Сул'
              ? 'text-red-500'
              : strength === 'Дунд'
              ? 'text-yellow-500'
              : 'text-green-600'
          }
        >
          {strength}
        </span>
      </div>

      <div className="h-2 w-full bg-gray-300 rounded-full overflow-hidden">
        <div
          className={`h-full transition-all duration-300 ${barColor}`}
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="bg-white rounded-md p-3 space-y-2">
        <p className="text-sm text-gray-600">Нууц үг дараах шаардлагыг хангах ёстой:</p>

        <Item ok={rules.hasNumber} label="1 тоо орсон байх" />
        <Item ok={rules.minLength} label="Хамгийн багадаа 12 тэмдэгт" />
        <Item ok={rules.hasUppercase} label="1 том үсэг орсон байх" />
        <Item ok={rules.hasSpecial} label="1 тусгай тэмдэгт орсон байх" />
      </div>
    </div>
  );
};
